import { Link } from 'react-router-dom';
import { Facebook, Instagram, Linkedin, Twitter, Youtube } from 'lucide-react';
import { BrandMark } from '@/components/common/BrandMark';
import { usePublicContent } from '@/hooks/api/usePublic';

const QUICK_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/plans', label: 'Plans' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/contact', label: 'Contact' },
] as const;

const LEGAL_LINKS = [
  { to: '/privacy-policy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms & Conditions' },
] as const;

export function Footer() {
  const { data } = usePublicContent();
  const s = data?.settings ?? {};
  const year = new Date().getFullYear();
  const companyName = s['company.name'] ?? 'DreamPick Private Limited';
  const gst = s['company.gst'] ?? '29AAMCD4327L1Z6';

  const socials: { href?: string; icon: React.ReactNode; label: string }[] = [
    { href: s['social.facebook'], icon: <Facebook className="h-4 w-4" />, label: 'Facebook' },
    { href: s['social.instagram'], icon: <Instagram className="h-4 w-4" />, label: 'Instagram' },
    { href: s['social.linkedin'], icon: <Linkedin className="h-4 w-4" />, label: 'LinkedIn' },
    { href: s['social.youtube'], icon: <Youtube className="h-4 w-4" />, label: 'YouTube' },
    { href: s['social.x'], icon: <Twitter className="h-4 w-4" />, label: 'X' },
  ].filter((x) => Boolean(x.href));

  return (
    <footer
      className="relative overflow-hidden border-t border-border/40 bg-secondary/50 text-secondary-foreground backdrop-blur-sm"
      data-testid="site-footer"
    >
      <div className="absolute inset-0 opacity-[0.03] bg-grain-noise mix-blend-overlay" aria-hidden />
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-64 w-[600px] rounded-full bg-gold/5 blur-3xl"
        aria-hidden
      />
      <div className="container relative py-16">
        <div className="grid gap-12 md:grid-cols-4">
          <div className="md:col-span-2">
            <BrandMark tone="light" />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-secondary-foreground/60">
              {s['company.tagline'] ??
                `${companyName} — premium electric mobility engineered in India.`}
            </p>
            <p className="mt-6 rounded-full border border-border/40 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-secondary-foreground/40 inline-flex items-center gap-2">
              GSTIN
              <span className="text-secondary-foreground/70">{gst}</span>
            </p>
            {socials.length > 0 && (
              <ul className="mt-6 flex gap-2" aria-label="Social media">
                {socials.map((sc) => (
                  <li key={sc.label}>
                    <a
                      href={sc.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={sc.label}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border/40 bg-white/5 text-secondary-foreground/60 transition-all duration-200 hover:border-gold/30 hover:bg-gold/10 hover:text-gold hover:scale-105"
                      data-testid={`social-${sc.label.toLowerCase()}`}
                    >
                      {sc.icon}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <FooterCol title="Quick links" items={QUICK_LINKS} />
          <FooterCol title="Legal" items={LEGAL_LINKS} />
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-border/30 pt-8 text-xs text-secondary-foreground/50 sm:flex-row">
          <p>
            © {year} {companyName}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({
  title,
  items,
}: {
  title: string;
  items: readonly { to: string; label: string }[];
}) {
  return (
    <div>
      <h4 className="mb-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-secondary-foreground/40">
        {title}
      </h4>
      <ul className="space-y-2.5 text-sm">
        {items.map((item) => (
          <li key={item.to}>
            <Link
              to={item.to}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-block text-secondary-foreground/60 transition-all duration-200 hover:text-gold hover:translate-x-1"
              data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
